/**
 * Layout Provider
 * 
 * React context provider that manages the active layout theme.
 * Reads layout settings from database and provides layout configuration
 * to all components via the useLayout hook.
 */

import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { siteSettingsService } from '@/services/siteSettingsService';
import { queryKeys } from './query-config';
import { 
  type LayoutTheme, 
  type LayoutPresetId, 
  type LayoutContextType,
  type NavigationStyle,
  type HeroStyle,
  type ContentLayoutStyle,
  type FooterStyle,
} from './layout-types';
import { getLayoutPreset, getDefaultPreset, isValidPresetId } from './layout-presets';
import { toast } from 'sonner';

// ============================================
// CONTEXT
// ============================================

const LayoutContext = createContext<LayoutContextType | null>(null);

// ============================================
// PROVIDER COMPONENT
// ============================================

type LayoutProviderProps = {
  children: React.ReactNode;
};

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const { data: siteSettings, isLoading: settingsLoading } = useSiteSettings();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  // Extract layout preset from settings
  const presetId = useMemo<LayoutPresetId>(() => {
    const layoutSettings = siteSettings?.features?.layout as { preset?: string } | undefined;
    if (layoutSettings?.preset && isValidPresetId(layoutSettings.preset)) {
      return layoutSettings.preset;
    }
    return 'classic';
  }, [siteSettings?.features?.layout]);

  // Get the full layout theme based on preset
  const currentLayout = useMemo<LayoutTheme>(() => {
    return getLayoutPreset(presetId);
  }, [presetId]);

  // Apply layout-specific CSS classes to body
  useEffect(() => {
    const body = document.body;
    
    // Remove all layout classes (including new ones)
    const allLayoutClasses = [
      'layout-classic',
      'layout-modern-sidebar',
      'layout-minimal-clean',
      'layout-corporate',
      'layout-magazine',
      'layout-landing-page',
      'layout-dashboard',
      'layout-portfolio',
      'layout-saas-product',
      'layout-ecommerce',
      'layout-nonprofit',
      'layout-education',
      'layout-healthcare',
      'layout-restaurant',
      'layout-agency',
      'layout-community',
      'layout-blog-focus',
    ];
    
    const allNavClasses = [
      'nav-sidebar-left',
      'nav-sidebar-collapsible',
      'nav-top-bar',
      'nav-centered-split',
      'nav-mega-menu'
    ];
    
    body.classList.remove(...allLayoutClasses, ...allNavClasses);

    // Add current layout class
    body.classList.add(`layout-${presetId}`);
    body.classList.add(`nav-${currentLayout.navigation.style}`);

  }, [presetId, currentLayout.navigation.style]);

  // Function to change layout
  const setLayout = useCallback(async (newPresetId: LayoutPresetId) => {
    if (newPresetId === presetId) {
      toast.info('This layout is already active');
      return;
    }

    if (!isValidPresetId(newPresetId)) {
      console.error('Invalid layout preset ID:', newPresetId);
      toast.error('Invalid layout preset');
      return;
    }

    setIsSaving(true);
    try {
      console.log('[LayoutProvider] Updating layout to:', newPresetId);
      
      const result = await siteSettingsService.updateFeatures({
        layout: { preset: newPresetId }
      });
      
      if (!result.success) {
        console.error('[LayoutProvider] Update failed:', result.error);
        const errorMessage = result.error?.getUserMessage?.() || result.error?.message || 'Failed to update layout';
        throw new Error(errorMessage);
      }
      
      console.log('[LayoutProvider] Update successful, invalidating queries');
      
      // Invalidate site settings query to refetch
      await queryClient.invalidateQueries({ queryKey: queryKeys.siteSettings });
      
      // Force a refetch to ensure UI updates
      await queryClient.refetchQueries({ queryKey: queryKeys.siteSettings });
      
      // Small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const layoutName = getLayoutPreset(newPresetId).name;
      toast.success(`Layout changed to ${layoutName}`);
      
      console.log('[LayoutProvider] Layout change complete');
    } catch (error) {
      console.error('[LayoutProvider] Error changing layout:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to change layout';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [presetId, queryClient]);

  const contextValue = useMemo<LayoutContextType>(() => ({
    currentLayout,
    presetId,
    isLoading: settingsLoading || isSaving,
    setLayout,
  }), [currentLayout, presetId, settingsLoading, isSaving, setLayout]);

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

/**
 * Hook to access the current layout theme
 */
export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  
  if (!context) {
    // Return default layout if used outside provider (fallback)
    return {
      currentLayout: getDefaultPreset(),
      presetId: 'classic',
      isLoading: false,
      setLayout: async () => {
        console.warn('useLayout: setLayout called outside of LayoutProvider');
      },
    };
  }
  
  return context;
};

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook to get just the navigation style
 */
export const useNavigationStyle = (): NavigationStyle => {
  const { currentLayout } = useLayout();
  return currentLayout.navigation.style;
};

/**
 * Hook to get just the hero style
 */
export const useHeroStyle = (): HeroStyle => {
  const { currentLayout } = useLayout();
  return currentLayout.hero.style;
};

/**
 * Hook to get just the content layout style
 */
export const useContentLayoutStyle = (): ContentLayoutStyle => {
  const { currentLayout } = useLayout();
  return currentLayout.content.style;
};

/**
 * Hook to get just the footer style
 */
export const useFooterStyle = (): FooterStyle => {
  const { currentLayout } = useLayout();
  return currentLayout.footer.style;
};

export default LayoutProvider;




